import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Game, HoleScore, TeamSide } from '../data/types';
import { VINOR_HOLES } from '../data/course';
import { buildScoreMaps, calculateMatchPlay } from '../engine/scoring';
import { useTournament } from '../context/TournamentContext';
import { useAuth } from '../context/AuthContext';
import { getPlayerSide } from '../engine/scoring';

interface GameScorecardProps {
  game: Game;
  onScoreChange?: (holeNumber: number, teamSide: TeamSide, strokes: number) => void;
  compact?: boolean;
}

interface ActiveCell {
  holeNumber: number;
  side: TeamSide;
  currentValue?: number;
}

interface PendingConfirm {
  holeNumber: number;
  side: TeamSide;
  strokes: number;
  resultText: string;
  winnerLabel: string;
}

export function GameScorecard({ game, onScoreChange, compact = false }: GameScorecardProps) {
  const { getScoresByGame, getPlayer, deleteScore, getMatchUp } = useTournament();
  const { user } = useAuth();
  const scores = getScoresByGame(game.id);
  const { sideA, sideB } = buildScoreMaps(scores);
  const matchState = calculateMatchPlay(sideA, sideB);

  const [activeCell, setActiveCell] = useState<ActiveCell | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
  const numpadRef = useRef<HTMLDivElement>(null);

  // Determine if user can edit scores
  const isAdmin = user?.role === 'admin';
  const userSide = user?.player ? getPlayerSide(user.player.id, game) : null;
  const matchUp = getMatchUp(game.match_up_id);
  const isLocked = matchUp?.locked ?? false;
  // Admin can edit even finished games (unless locked). Player cannot edit finished games.
  const canEdit = !isLocked && (isAdmin || (!matchState.isFinished && user?.player && userSide !== null));

  // Close numpad on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (numpadRef.current && !numpadRef.current.contains(e.target as Node)) {
        setActiveCell(null);
      }
    }
    if (activeCell) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [activeCell]);

  // Side labels
  const sideAName1 = game.type === 'single'
    ? (game.player_a_id ? getPlayer(game.player_a_id)?.name : 'Hráč A') ?? 'Hráč A'
    : (game.foursome_a1_id ? getPlayer(game.foursome_a1_id)?.name : '?') ?? '?';
  const sideAName2 = game.type === 'foursome'
    ? (game.foursome_a2_id ? getPlayer(game.foursome_a2_id)?.name : '?') ?? '?'
    : null;

  const sideBName1 = game.type === 'single'
    ? (game.player_b_id ? getPlayer(game.player_b_id)?.name : 'Hráč B') ?? 'Hráč B'
    : (game.foursome_b1_id ? getPlayer(game.foursome_b1_id)?.name : '?') ?? '?';
  const sideBName2 = game.type === 'foursome'
    ? (game.foursome_b2_id ? getPlayer(game.foursome_b2_id)?.name : '?') ?? '?'
    : null;

  // Full text labels (for result banners)
  const sideALabel = sideAName2 ? `${sideAName1} + ${sideAName2}` : sideAName1;
  const sideBLabel = sideBName2 ? `${sideBName1} + ${sideBName2}` : sideBName1;

  // Infer gender from Czech surname (fallback when DB field is null)
  function isFemale(player: { gender?: 'M' | 'F' | null; name?: string } | null | undefined): boolean {
    if (!player) return false;
    if (player.gender === 'F') return true;
    if (player.gender === 'M') return false;
    // Heuristic: Czech female surnames end with -ová or -á
    const surname = player.name?.trim().split(' ').pop() ?? '';
    return surname.endsWith('ová') || surname.endsWith('á');
  }

  // Gender-aware verb: vyhrál / vyhrála / vyhráli / vyhrály
  function getWinVerb(side: 'a' | 'b'): string {
    if (game.type === 'single') {
      const playerId = side === 'a' ? game.player_a_id : game.player_b_id;
      const player = playerId ? getPlayer(playerId) : null;
      return isFemale(player) ? 'vyhrála' : 'vyhrál';
    }
    // Foursome — check both players
    const p1Id = side === 'a' ? game.foursome_a1_id : game.foursome_b1_id;
    const p2Id = side === 'a' ? game.foursome_a2_id : game.foursome_b2_id;
    const p1 = p1Id ? getPlayer(p1Id) : null;
    const p2 = p2Id ? getPlayer(p2Id) : null;
    if (isFemale(p1) && isFemale(p2)) return 'vyhrály';
    return 'vyhráli';
  }

  // Abbreviate name: "František Jindra" → "F. Jindra"
  function abbrev(name: string): string {
    const parts = name.trim().split(' ');
    if (parts.length <= 1) return name;
    return `${parts[0][0]}. ${parts.slice(1).join(' ')}`;
  }

  // Running match state per hole
  function getRunningStatus(upToHole: number): { text: string; color: string } {
    const tempA = new Map<number, number>();
    const tempB = new Map<number, number>();
    for (let h = 1; h <= upToHole; h++) {
      const a = sideA.get(h);
      const b = sideB.get(h);
      if (a !== undefined) tempA.set(h, a);
      if (b !== undefined) tempB.set(h, b);
    }
    const state = calculateMatchPlay(tempA, tempB);
    if (state.lead === 0) return { text: 'A/S', color: 'var(--text-muted)' };
    if (state.isFinished) return { text: state.resultText ?? '', color: state.winnerSide === 'a' ? 'var(--side-a)' : 'var(--side-b)' };
    if (state.leaderSide === 'a') return { text: `${state.lead}UP`, color: 'var(--side-a)' };
    return { text: `${state.lead}UP`, color: 'var(--side-b)' };
  }

  function getHoleClass(holeNum: number): string {
    const a = sideA.get(holeNum);
    const b = sideB.get(holeNum);
    if (a === undefined || b === undefined) return '';
    if (a < b) return 'hole-won';
    if (a > b) return 'hole-lost';
    return 'hole-halved';
  }

  /** Get golf mark class for a score relative to par */
  function getGolfMark(strokes: number | undefined, par: number): string {
    if (strokes === undefined) return '';
    const diff = strokes - par;
    if (diff <= -2) return 'golf-eagle';
    if (diff === -1) return 'golf-birdie';
    if (diff === 0) return 'golf-par';
    if (diff === 1) return 'golf-bogey';
    if (diff === 2) return 'golf-double-bogey';
    return 'golf-triple-bogey';
  }

  function handleCellClick(holeNumber: number, side: TeamSide) {
    const scoreMap = side === 'a' ? sideA : sideB;
    setActiveCell({
      holeNumber,
      side,
      currentValue: scoreMap.get(holeNumber),
    });
  }

  /** Simulate what match state would be if we save this score */
  function simulateMatchState(holeNumber: number, side: TeamSide, strokes: number) {
    const simA = new Map(sideA);
    const simB = new Map(sideB);
    if (side === 'a') simA.set(holeNumber, strokes);
    else simB.set(holeNumber, strokes);
    return calculateMatchPlay(simA, simB);
  }

  function handleNumpadSelect(strokes: number) {
    if (!activeCell || !onScoreChange) return;

    // If user is NOT admin, check if this score would finish the game
    if (!isAdmin) {
      const simState = simulateMatchState(activeCell.holeNumber, activeCell.side, strokes);
      if (simState.isFinished) {
        // Show confirmation dialog
        const winLabel = simState.winnerSide === 'a' ? sideALabel
          : simState.winnerSide === 'b' ? sideBLabel
          : 'Remíza';
        const resultTxt = simState.winnerSide === 'halved'
          ? 'Halved'
          : `${winLabel} ${getWinVerb(simState.winnerSide as 'a' | 'b')} ${simState.resultText}`;
        setPendingConfirm({
          holeNumber: activeCell.holeNumber,
          side: activeCell.side,
          strokes,
          resultText: resultTxt,
          winnerLabel: winLabel,
        });
        setActiveCell(null);
        return;
      }
    }

    // No finish → save directly
    onScoreChange(activeCell.holeNumber, activeCell.side, strokes);
    setActiveCell(null);
  }

  function handleConfirmFinish() {
    if (pendingConfirm && onScoreChange) {
      onScoreChange(pendingConfirm.holeNumber, pendingConfirm.side, pendingConfirm.strokes);
    }
    setPendingConfirm(null);
  }

  function handleRejectFinish() {
    setPendingConfirm(null);
  }

  function handleDeleteScore() {
    if (activeCell) {
      deleteScore(game.id, activeCell.holeNumber, activeCell.side);
      setActiveCell(null);
    }
  }

  const holes = VINOR_HOLES;
  const frontNine = holes.filter(h => h.number <= 9);
  const backNine = holes.filter(h => h.number > 9);

  // Calculate totals
  const frontTotalA = frontNine.reduce((sum, h) => sum + (sideA.get(h.number) ?? 0), 0);
  const frontTotalB = frontNine.reduce((sum, h) => sum + (sideB.get(h.number) ?? 0), 0);
  const backTotalA = backNine.reduce((sum, h) => sum + (sideA.get(h.number) ?? 0), 0);
  const backTotalB = backNine.reduce((sum, h) => sum + (sideB.get(h.number) ?? 0), 0);

  // Get par for active cell
  const activePar = activeCell ? (holes.find(h => h.number === activeCell.holeNumber)?.par ?? 4) : 4;

  // Score button style helper
  function getScoreBtnClass(strokes: number, par: number): string {
    const diff = strokes - par;
    if (diff <= -2) return 'score-btn-eagle';
    if (diff === -1) return 'score-btn-birdie';
    if (diff === 0) return 'score-btn-par';
    if (diff === 1) return 'score-btn-bogey';
    return 'score-btn-double';
  }

  const renderHalfTable = (holeSet: typeof holes, label: string, totalA: number, totalB: number) => (
    <table style={{ tableLayout: 'fixed', width: '100%' }}>
      <thead>
        <tr>
          <th style={{ width: '18%', textAlign: 'left', paddingLeft: '0.5rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>Jamka</th>
          {holeSet.map(h => (
            <th key={h.number}>{h.number}</th>
          ))}
          <th>{label}</th>
        </tr>
      </thead>
      <tbody>
        {/* Par row */}
        <tr className="par-row">
          <td style={{ fontWeight: 700, fontFamily: 'var(--font-body)' }}>Par</td>
          {holeSet.map(h => (
            <td key={h.number}>{h.par}</td>
          ))}
          <td className="total-col">{holeSet.reduce((s, h) => s + h.par, 0)}</td>
        </tr>

        {/* Side A */}
        <tr>
          <td className="score-a" style={{
            fontFamily: 'var(--font-body)',
            fontWeight: 600,
            fontSize: '0.75rem',
            textAlign: 'left',
            paddingLeft: '0.5rem',
            lineHeight: sideAName2 ? 1.3 : 'inherit',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            <span className="name-full">{sideAName2 ? (<>{sideAName1}<br/>{sideAName2}</>) : sideAName1}</span>
            <span className="name-short">{sideAName2 ? (<>{abbrev(sideAName1)}<br/>{abbrev(sideAName2)}</>) : abbrev(sideAName1)}</span>
          </td>
          {holeSet.map(h => {
            const score = sideA.get(h.number);
            const golfMark = getGolfMark(score, h.par);
            const hClass = getHoleClass(h.number);
            const isEditable = canEdit;
            const isActive = activeCell?.holeNumber === h.number && activeCell?.side === 'a';
            return (
              <td
                key={h.number}
                className={`${hClass} ${isEditable ? 'score-cell-editable' : ''} ${isActive ? 'score-cell-active' : ''}`}
                onClick={isEditable ? () => handleCellClick(h.number, 'a') : undefined}
              >
                {score !== undefined ? (
                  <span className={`golf-mark ${golfMark}`}>{score}</span>
                ) : (isEditable ? '·' : '')}
              </td>
            );
          })}
          <td className="total-col score-a">{totalA || ''}</td>
        </tr>

        {/* Side B */}
        <tr>
          <td className="score-b" style={{
            fontFamily: 'var(--font-body)',
            fontWeight: 600,
            fontSize: '0.75rem',
            textAlign: 'left',
            paddingLeft: '0.5rem',
            lineHeight: sideBName2 ? 1.3 : 'inherit',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            <span className="name-full">{sideBName2 ? (<>{sideBName1}<br/>{sideBName2}</>) : sideBName1}</span>
            <span className="name-short">{sideBName2 ? (<>{abbrev(sideBName1)}<br/>{abbrev(sideBName2)}</>) : abbrev(sideBName1)}</span>
          </td>
          {holeSet.map(h => {
            const score = sideB.get(h.number);
            const golfMark = getGolfMark(score, h.par);
            const hClass = getHoleClass(h.number);
            const isEditable = canEdit;
            const isActive = activeCell?.holeNumber === h.number && activeCell?.side === 'b';
            return (
              <td
                key={h.number}
                className={`${hClass} ${isEditable ? 'score-cell-editable' : ''} ${isActive ? 'score-cell-active' : ''}`}
                onClick={isEditable ? () => handleCellClick(h.number, 'b') : undefined}
              >
                {score !== undefined ? (
                  <span className={`golf-mark ${golfMark}`}>{score}</span>
                ) : (isEditable ? '·' : '')}
              </td>
            );
          })}
          <td className="total-col score-b">{totalB || ''}</td>
        </tr>

        {/* Match status row */}
        {!compact && (
          <tr>
            <td style={{ fontWeight: 700, fontFamily: 'var(--font-body)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Status
            </td>
            {holeSet.map(h => {
              const a = sideA.get(h.number);
              const b = sideB.get(h.number);
              if (a === undefined || b === undefined) return <td key={h.number}></td>;
              const status = getRunningStatus(h.number);
              return (
                <td key={h.number} className="match-status" style={{ fontSize: '0.65rem', color: status.color, fontWeight: 700 }}>
                  {status.text}
                </td>
              );
            })}
            <td></td>
          </tr>
        )}
      </tbody>
    </table>
  );

  return (
    <div className="scorecard" style={{ position: 'relative' }}>
      {renderHalfTable(frontNine, 'OUT', frontTotalA, frontTotalB)}
      <div style={{ height: '0.5rem' }} />
      {renderHalfTable(backNine, 'IN', backTotalA, backTotalB)}

      {/* Numpad overlay — portalled to body to avoid iOS fixed-inside-relative bug */}
      {activeCell && createPortal(
        <div className="numpad-overlay">
          <div className="numpad-backdrop" onClick={() => setActiveCell(null)} />
          <div className="numpad-panel" ref={numpadRef}>
            <div className="numpad-header">
              <span className="numpad-title">
                Jamka {activeCell.holeNumber} • {activeCell.side === 'a' ? sideALabel : sideBLabel}
              </span>
              <span className="numpad-par">Par {activePar}</span>
            </div>
            <div className="numpad-grid">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
                <button
                  key={n}
                  className={`numpad-btn ${getScoreBtnClass(n, activePar)} ${activeCell.currentValue === n ? 'numpad-btn-selected' : ''}`}
                  onClick={() => handleNumpadSelect(n)}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="numpad-actions">
              {activeCell.currentValue && (
                <button className="numpad-delete" onClick={handleDeleteScore}>
                  Smazat
                </button>
              )}
              <button className="numpad-cancel" onClick={() => setActiveCell(null)}>
                Zrušit
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Confirmation dialog – finish game */}
      {pendingConfirm && createPortal(
        <div className="numpad-overlay">
          <div className="numpad-backdrop" onClick={handleRejectFinish} />
          <div className="numpad-panel" style={{ maxWidth: '340px', textAlign: 'center' }}>
            <div style={{ padding: '1.75rem 1.25rem' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🏁</div>
              <h3 style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1.125rem',
                marginBottom: '1rem',
                color: 'var(--text-primary)',
                letterSpacing: '0.02em',
              }}>
                Ukončení zápasu
              </h3>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.75rem', lineHeight: 1.6 }}>
                Tímto zápisem byl zápas ukončen s výsledkem:
              </p>
              <div style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1.25rem',
                fontWeight: 800,
                color: 'var(--accent)',
                padding: '0.875rem 1rem',
                background: 'var(--bg-surface)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                marginBottom: '1rem',
              }}>
                {pendingConfirm.resultText}
              </div>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                Je tento výsledek správný? Po potvrzení již nebude možné výsledky editovat.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                <button
                  onClick={handleConfirmFinish}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1rem',
                    borderRadius: 'var(--radius-md)',
                    border: 'none',
                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                    color: 'white',
                    fontSize: '0.9375rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    boxShadow: '0 4px 14px rgba(34, 197, 94, 0.3)',
                    transition: 'all 0.2s ease',
                  }}
                >
                  ✓ Potvrdit a uzavřít
                </button>
                <button
                  onClick={handleRejectFinish}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-light)',
                    background: 'transparent',
                    color: 'var(--text-secondary)',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  ← Zpět k úpravě
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Match result */}
      {matchState.isFinished && (
        <div
          style={{
            textAlign: 'center',
            padding: '1rem',
            marginTop: '0.75rem',
            background: 'var(--bg-surface)',
            borderRadius: 'var(--radius-md)',
            fontFamily: 'var(--font-heading)',
            fontSize: '1.25rem',
            fontWeight: 800,
            color: matchState.winnerSide === 'halved' ? 'var(--text-secondary)' : 'var(--accent)',
          }}
        >
          {matchState.winnerSide === 'a' && `${sideALabel} ${getWinVerb('a')} ${matchState.resultText}`}
          {matchState.winnerSide === 'b' && `${sideBLabel} ${getWinVerb('b')} ${matchState.resultText}`}
          {matchState.winnerSide === 'halved' && `Remíza — Halved`}
        </div>
      )}

      {!matchState.isFinished && matchState.holesPlayed > 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: '0.75rem',
            marginTop: '0.75rem',
            background: 'rgba(245, 158, 11, 0.1)',
            borderRadius: 'var(--radius-md)',
            fontFamily: 'var(--font-heading)',
            fontSize: '1rem',
            fontWeight: 700,
            color: 'var(--live)',
          }}
        >
          {matchState.lead === 0 ? 'A/S' : (
            <>
              {matchState.leaderSide === 'a' ? sideALabel : sideBLabel} vede{' '}
              {matchState.lead} UP
              {matchState.isDormie && ' (DORMIE)'}
              {` • Jamka ${matchState.holesPlayed}/${18}`}
            </>
          )}
        </div>
      )}
    </div>
  );
}
